<script>
  import { createEventDispatcher } from 'svelte';
  import { X, FileText, Plus, Trash2 } from 'lucide-svelte';
  import { variantsApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import { autosize } from '../lib/actions/autosize.js';

  export let proposal = null;
  export let customers = [];
  export let loading = false;

  let error = '';
  let loadingVariants = false;

  // Form fields
  let formData = {
    title: '',
    description: '',
    customerId: '',
    proposedPrice: '',
    discountPercentage: '0',
    notes: '',
    items: [] // Array of { variantId, quantity, unitPrice, discountAmount, description }
  };

  // Available data
  let allVariants = [];

  // Billing cycle labels
  const BILLING_CYCLES = {
    one_time: 'Una tantum',
    monthly: 'Mensile',
    bimonthly: 'Bimestrale',
    quarterly: 'Trimestrale',
    yearly: 'Annuale'
  };

  const dispatch = createEventDispatcher();

  // Group variants by product for display
  $: groupedVariants = allVariants.reduce((groups, variant) => {
    const productName = variant.product?.name || 'Altro';
    if (!groups[productName]) {
      groups[productName] = [];
    }
    groups[productName].push(variant);
    return groups;
  }, {});

  // Calculate total from items (accounting for discounts)
  $: calculatedTotal = formData.items.reduce((sum, item) => {
    if (!item.variantId) return sum;
    const variant = allVariants.find(v => v.id === parseInt(item.variantId));
    const manual = manualUnitPrice(item);
    // A typed price is the line's price whether or not the catalogue row is
    // loaded: a variant the list has not returned would otherwise silently drop
    // a line the user has just priced by hand.
    if (manual === null && !variant) return sum;
    const price = manual !== null ? manual : getEffectivePrice(variant);
    const quantity = parseFloat(item.quantity || 0);
    const discount = parseFloat(item.discountAmount || 0);
    // Discount is per unit, so subtract discount * quantity from total
    return sum + ((price - discount) * quantity);
  }, 0);

  // Initialize form when component mounts or proposal changes
  let initialized = false;

  $: if (proposal && !initialized) {
    // Edit mode - populate form with proposal data
    console.log('[ProposalModal] Initializing form with proposal:', proposal);
    formData = {
      title: proposal.title || '',
      description: proposal.description || '',
      customerId: proposal.customer_id || '',
      proposedPrice: proposal.proposed_price?.toString() || '',
      discountPercentage: proposal.discount_percentage?.toString() || '0',
      notes: proposal.notes || '',
      items: proposal.items ? proposal.items.map(item => ({
        variantId: item.variant_id?.toString() || '',
        quantity: item.quantity?.toString() || '1',
        // Only a manually priced line comes back into the input. A stored
        // catalogue price left in the field would be sent back as a manual one
        // and freeze the line at the price it happened to have.
        unitPrice: item.is_manual_price ? parseFloat(item.unit_price).toString() : '',
        discountAmount: item.discount_amount ? parseFloat(item.discount_amount).toString() : '',
        description: item.description || ''
      })) : []
    };
    initialized = true;
    error = '';
    loadVariants();
  } else if (!proposal && !initialized) {
    // Create mode
    console.log('[ProposalModal] Initializing empty form for new proposal');
    resetForm();
    initialized = true;
    error = '';
    loadVariants();
  }

  async function loadVariants() {
    try {
      loadingVariants = true;
      const params = { active: true };

      // If customer is selected, include customerId to get customer-specific prices
      if (formData.customerId) {
        params.customerId = formData.customerId;
      }

      const variantsData = await variantsApi.getAll(params);
      allVariants = variantsData || [];
      console.log('[ProposalModal] Loaded variants for customer:', formData.customerId || '(none)', 'count:', allVariants.length);

    } catch (err) {
      console.error('[ProposalModal] Load variants error:', err);
    } finally {
      loadingVariants = false;
    }
  }

  // Reload variants when customer changes
  let previousCustomerId = null;
  $: if (formData.customerId !== previousCustomerId) {
    previousCustomerId = formData.customerId;
    loadVariants();
  }

  function resetForm() {
    formData = {
      title: '',
      description: '',
      customerId: '',
      proposedPrice: '',
      discountPercentage: '0',
      notes: '',
      items: []
    };
  }

  function handleClose() {
    if (loading) return;
    resetForm();
    initialized = false;
    dispatch('close');
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && !loading) {
      handleClose();
    }
  }

  function addItem() {
    formData.items = [...formData.items, { variantId: '', quantity: '1', unitPrice: '', discountAmount: '', description: '' }];
  }

  /**
   * The price typed on a line, or null when the line follows the catalogue.
   * An empty numeric input reaches here as '', null or undefined depending on
   * the browser, and all three mean the same thing.
   *
   * @param {Object} item - One row of formData.items
   * @returns {number|null}
   */
  function manualUnitPrice(item) {
    const parsed = parseFloat(item.unitPrice);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Catalogue price of the variant a line points at, for the input placeholder.
   * `variants` is an explicit argument, never read from the component-level
   * `allVariants`: `allVariants` arrives async, after first render, and a
   * closure over it here would repeat the untrack bug this replaces.
   *
   * @param {Object} item - One row of formData.items
   * @param {Array} variants - The currently loaded variant catalogue
   * @returns {string}
   */
  function cataloguePlaceholder(item, variants) {
    if (!item.variantId) return $_('proposals.modal.unitPricePlaceholder');
    const variant = variants.find(v => v.id === parseInt(item.variantId));
    if (!variant) return $_('proposals.modal.unitPricePlaceholder');
    return getEffectivePrice(variant).toFixed(2);
  }

  /**
   * Whether a line would go out priced at zero: a variant is selected, no
   * manual price overrides it, and the catalogue has nothing positive to
   * charge (list price NULL/zero, or a zero negotiated price for this
   * customer). A variant not yet in the loaded list is unknown, not zero,
   * and is not flagged.
   *
   * @param {Object} item - One row of formData.items
   * @param {Array} variants - The currently loaded variant catalogue
   * @returns {boolean}
   */
  function needsPriceWarning(item, variants) {
    if (!item.variantId) return false;
    if (manualUnitPrice(item) !== null) return false;
    const variant = variants.find(v => v.id === parseInt(item.variantId));
    if (!variant) return false;
    const effective = getEffectivePrice(variant);
    return !Number.isFinite(effective) || effective <= 0;
  }

  function removeItem(index) {
    formData.items = formData.items.filter((_, i) => i !== index);
  }

  function formatPrice(price) {
    if (price === null || price === undefined) return '-';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '-';
    return `€${numPrice.toFixed(2)}`;
  }

  function getEffectivePrice(variant) {
    // Use customerPrice if available (custom price), otherwise use base price
    if (variant.customerPrice !== null && variant.customerPrice !== undefined) {
      return parseFloat(variant.customerPrice);
    }
    return parseFloat(variant.price) || 0;
  }

  function hasCustomPrice(variant) {
    return variant.customerPrice !== null && variant.customerPrice !== undefined;
  }

  function useCalculatedPrice() {
    formData.proposedPrice = calculatedTotal.toFixed(2);
  }

  /**
   * A row nobody touched: the operator clicked "Aggiungi" and left everything
   * at its default. Dropping it silently keeps an unused row from blocking
   * save, the same way the old `item.variantId && quantity > 0` filter did
   * for a plain empty row.
   *
   * @param {Object} item - One row of formData.items
   * @returns {boolean}
   */
  function isRowBlank(item) {
    const quantityIsDefault = item.quantity === '' || item.quantity === null || item.quantity === undefined || parseFloat(item.quantity) === 1;
    return !item.variantId
      && manualUnitPrice(item) === null
      && !item.discountAmount
      && !(item.description && item.description.trim())
      && quantityIsDefault;
  }

  function handleSubmit(event) {
    event.preventDefault();

    try {
      error = '';

      // Validate required fields
      if (!formData.title.trim()) {
        error = $_('proposals.validation.titleRequired');
        return;
      }

      // A row is either fully blank (dropped silently) or has to point at a
      // real, positively-quantified variant: a price, discount or description
      // typed onto a row with no variant, or a variant with a blank or
      // non-positive quantity, is a mistake worth stopping the save for, not
      // a value worth guessing at.
      const validItems = [];
      let index = 0;
      let invalidRowFound = false;
      for (index = 0; index < formData.items.length; index += 1) {
        const item = formData.items[index];
        if (isRowBlank(item)) continue;

        const quantity = parseFloat(item.quantity);
        if (!item.variantId || !(quantity > 0)) {
          invalidRowFound = true;
          break;
        }

        validItems.push({
          variantId: parseInt(item.variantId),
          quantity,
          // null asks the backend for the catalogue price; a number overrides it
          // and marks the line manual.
          unitPrice: manualUnitPrice(item),
          discountAmount: item.discountAmount ? parseFloat(item.discountAmount) : 0,
          description: item.description?.trim() || null
        });
      }

      if (invalidRowFound) {
        error = $_('proposals.validation.itemInvalid', { values: { row: index + 1 } });
        return;
      }

      // Prepare data
      const data = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        customerId: formData.customerId || null,
        // formData.proposedPrice is a Number once bound from a type="number"
        // input, so `formData.proposedPrice ? x : null` silently turned a
        // typed 0 into a request to recompute from the catalogue.
        proposedPrice: (formData.proposedPrice === '' || formData.proposedPrice === null || formData.proposedPrice === undefined)
          ? null
          : parseFloat(formData.proposedPrice),
        discountPercentage: parseFloat(formData.discountPercentage) || 0,
        notes: formData.notes.trim() || null,
        items: validItems
      };

      console.log('[ProposalModal] Saving proposal:', data);

      dispatch('save', data);

    } catch (err) {
      error = err.message;
      console.error('[ProposalModal] Save error:', err);
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- Modal backdrop -->
<div class="fixed inset-0 z-50 flex items-center justify-center">
  <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" on:click={handleClose}></div>

  <!-- Modal -->
  <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-3xl w-full mx-4 scale-in">
    <!-- Header -->
    <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between bg-linear-to-r from-primary-50/50 dark:from-primary-900/30 to-transparent rounded-t-2xl">
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
          <FileText class="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 class="text-lg font-bold text-gray-900 dark:text-white">
            {proposal ? $_('proposals.modal.editTitle') : $_('proposals.modal.createTitle')}
          </h2>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {proposal ? $_('proposals.modal.editSubtitle') : $_('proposals.modal.createSubtitle')}
          </p>
        </div>
      </div>
      {#if !loading}
        <button
          on:click={handleClose}
          class="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
        >
          <X size={20} />
        </button>
      {/if}
    </div>

    <!-- Content -->
    <form on:submit={handleSubmit}>
      <div class="px-6 py-4 max-h-[70vh] overflow-y-auto">
        {#if error}
          <div class="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-3">
            <div class="text-sm text-red-800 dark:text-red-300">{error}</div>
          </div>
        {/if}

        <div class="space-y-4">
          <!-- Title -->
          <div>
            <label for="title" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {$_('proposals.fields.title')} *
            </label>
            <input
              id="title"
              type="text"
              bind:value={formData.title}
              placeholder={$_('proposals.modal.titlePlaceholder')}
              class="input w-full"
              disabled={loading}
              required
            />
          </div>

          <!-- Description -->
          <div>
            <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {$_('proposals.fields.description')}
            </label>
            <textarea
              id="description"
              bind:value={formData.description}
              placeholder={$_('proposals.modal.descriptionPlaceholder')}
              class="input w-full"
              use:autosize={{ minRows: 4, maxRows: 16, value: formData.description }}
              disabled={loading}
              aria-describedby="description-hint"
            ></textarea>
            <p id="description-hint" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {$_('proposals.modal.descriptionHint')}
            </p>
          </div>

          <!-- Customer -->
          <div>
            <label for="customer" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {$_('proposals.fields.customer')}
            </label>
            <select
              id="customer"
              bind:value={formData.customerId}
              class="input w-full"
              disabled={loading}
            >
              <option value="">{$_('proposals.modal.noCustomer')}</option>
              {#each customers as customer}
                <option value={customer.id}>
                  {customer.companyName || customer.name || customer.id}
                </option>
              {/each}
            </select>
            {#if formData.customerId}
              <p class="text-xs text-primary-600 dark:text-primary-400 mt-1">
                ★ = Prezzo personalizzato per questo cliente
              </p>
            {/if}
          </div>

          <!-- Items -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {$_('proposals.fields.items')}
              </label>
              <button
                type="button"
                on:click={addItem}
                class="btn btn-sm btn-secondary"
                disabled={loading}
              >
                <Plus size={14} class="mr-1" />
                {$_('common.add')}
              </button>
            </div>

            {#if formData.items.length === 0}
              <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                <p class="text-sm text-gray-500 dark:text-gray-400">{$_('proposals.modal.noItems')}</p>
                <button
                  type="button"
                  on:click={addItem}
                  class="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                  disabled={loading}
                >
                  {$_('proposals.modal.addFirstItem')}
                </button>
              </div>
            {:else}
              <!-- Desktop-only column header. Widths match the row cells below
                   so the columns line up; there is no equivalent on narrow
                   screens, where each field carries its own visible label. -->
              <div class="hidden sm:flex items-center gap-2 mb-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                <div class="flex-1">{$_('proposals.modal.itemsHeaderVariant')}</div>
                <div class="w-24">{$_('proposals.modal.itemsHeaderQuantity')}</div>
                <div class="w-32">{$_('proposals.modal.itemsHeaderUnitPrice')}</div>
                <div class="w-32">{$_('proposals.modal.itemsHeaderDiscount')}</div>
                <div class="w-9" aria-hidden="true"></div>
              </div>
              <div class="space-y-2">
                {#each formData.items as item, index}
                  <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div class="flex flex-col gap-2 sm:flex-row sm:items-center mb-2">
                      <div class="flex items-center gap-2 sm:contents">
                        <div class="flex-1">
                          <select
                            bind:value={item.variantId}
                            class="input w-full"
                            disabled={loading || loadingVariants}
                          >
                            <option value="">{loadingVariants ? 'Caricamento...' : 'Seleziona variante...'}</option>
                            {#each Object.entries(groupedVariants) as [productName, variants]}
                              <optgroup label={productName}>
                                {#each variants as v}
                                  <option value={v.id.toString()}>
                                    {v.code} - {v.name} ({formatPrice(getEffectivePrice(v))}/{BILLING_CYCLES[v.billingCycle] || v.billingCycle}){hasCustomPrice(v) ? ' ★' : ''}
                                  </option>
                                {/each}
                              </optgroup>
                            {/each}
                          </select>
                        </div>
                        <button
                          type="button"
                          on:click={() => removeItem(index)}
                          class="sm:hidden p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          disabled={loading}
                          title="Rimuovi"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div class="grid grid-cols-3 gap-2 sm:contents">
                        <div>
                          <label for={`quantity-${index}`} class="sm:hidden block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {$_('proposals.modal.itemsHeaderQuantity')}
                          </label>
                          <input
                            id={`quantity-${index}`}
                            type="number"
                            bind:value={item.quantity}
                            min="0.001"
                            step="0.001"
                            placeholder="Qtà"
                            class="input w-full sm:w-24 pr-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            disabled={loading}
                          />
                        </div>
                        <div>
                          <label for={`unit-price-${index}`} class="sm:hidden block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {$_('proposals.modal.itemsHeaderUnitPrice')}
                          </label>
                          <div class="relative">
                            <span class="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">€</span>
                            <input
                              id={`unit-price-${index}`}
                              type="number"
                              bind:value={item.unitPrice}
                              min="0"
                              step="0.01"
                              placeholder={cataloguePlaceholder(item, allVariants)}
                              class="input w-full sm:w-32 pl-6 pr-1 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none {needsPriceWarning(item, allVariants) ? 'ring-1 ring-amber-500 focus:ring-amber-500' : ''}"
                              disabled={loading}
                              title={$_('proposals.fields.unitPrice')}
                              aria-label={$_('proposals.fields.unitPrice')}
                              aria-describedby={manualUnitPrice(item) !== null ? `manual-hint-${index}` : (needsPriceWarning(item, allVariants) ? `price-warning-${index}` : undefined)}
                            />
                          </div>
                        </div>
                        <div>
                          <label for={`discount-${index}`} class="sm:hidden block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {$_('proposals.modal.itemsHeaderDiscount')}
                          </label>
                          <div class="relative">
                            <span class="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">-€</span>
                            <input
                              id={`discount-${index}`}
                              type="number"
                              bind:value={item.discountAmount}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              class="input w-full sm:w-32 pl-7 pr-1 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              disabled={loading}
                              title={$_('proposals.fields.itemDiscount')}
                              aria-label={$_('proposals.fields.itemDiscount')}
                            />
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        on:click={() => removeItem(index)}
                        class="hidden sm:inline-flex p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        disabled={loading}
                        title="Rimuovi"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <!-- A line whose price the operator typed no longer follows
                         the catalogue: it has to be visible on the row, or the
                         next person to open the proposal cannot tell why one
                         price did not move with the listino. The zero-price
                         warning takes the same spot when there is no manual
                         price: the two never apply to the same row. -->
                    {#if manualUnitPrice(item) !== null}
                      <p id={`manual-hint-${index}`} class="text-xs text-magenta-600 dark:text-magenta-400 mb-2">
                        {$_('proposals.modal.manualPriceHint')}
                      </p>
                    {:else if needsPriceWarning(item, allVariants)}
                      <p id={`price-warning-${index}`} class="text-xs text-amber-600 dark:text-amber-400 mb-2">
                        {$_('proposals.modal.priceWarningHint')}
                      </p>
                    {/if}
                    <input
                      type="text"
                      bind:value={item.description}
                      placeholder={$_('proposals.modal.itemDescriptionPlaceholder')}
                      class="input w-full text-sm"
                      disabled={loading}
                    />
                  </div>
                {/each}
              </div>
            {/if}

            {#if formData.items.length > 0 && calculatedTotal > 0}
              <div class="mt-2 flex items-center justify-between text-sm">
                <span class="text-gray-500 dark:text-gray-400">Totale calcolato:</span>
                <div class="flex items-center space-x-2">
                  <span class="font-medium text-gray-900 dark:text-white">{formatPrice(calculatedTotal)}</span>
                  <button
                    type="button"
                    on:click={useCalculatedPrice}
                    class="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                  >
                    Usa come prezzo
                  </button>
                </div>
              </div>
            {/if}
          </div>

          <!-- Pricing -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="proposedPrice" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {$_('proposals.fields.proposedPrice')}
              </label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">€</span>
                <input
                  id="proposedPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  bind:value={formData.proposedPrice}
                  placeholder="0.00"
                  class="input w-full pl-8"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label for="discount" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {$_('proposals.fields.discount')}
              </label>
              <div class="relative">
                <input
                  id="discount"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  bind:value={formData.discountPercentage}
                  placeholder="0"
                  class="input w-full pr-8"
                  disabled={loading}
                />
                <span class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">%</span>
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div>
            <label for="notes" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {$_('proposals.fields.notes')}
            </label>
            <textarea
              id="notes"
              bind:value={formData.notes}
              placeholder={$_('proposals.modal.notesPlaceholder')}
              class="input w-full"
              use:autosize={{ minRows: 2, maxRows: 8, value: formData.notes }}
              disabled={loading}
              aria-describedby="notes-hint"
            ></textarea>
            <p id="notes-hint" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {$_('proposals.modal.notesHint')}
            </p>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 rounded-b-2xl">
        <button
          type="button"
          on:click={handleClose}
          class="btn btn-secondary"
          disabled={loading}
        >
          {$_('common.cancel')}
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          disabled={loading || !formData.title.trim()}
        >
          {#if loading}
            <svg class="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          {/if}
          {proposal ? $_('common.update') : $_('common.create')}
        </button>
      </div>
    </form>
  </div>
</div>
